/**
 * Cookie utility fonksiyonları
 * Oda bazlı travelers bilgisi için cookie yönetimi
 * SIFIRDAN YENİDEN YAZILDI - Geriye dönük uyumluluk YOK
 */

import type { TravelersPreferences } from '@/types/travelers'

const COOKIE_NAME = 'travelers_preferences_v2'; // Yeni cookie key (eski sistemle karışmaması için)
const COOKIE_MAX_AGE = 86400; // 24 saat (saniye cinsinden)

/**
 * Cookie'ye travelers bilgisi kaydet
 * 
 * @param preferences - Travelers bilgisi objesi (oda bazlı)
 */
export function setTravelersPreferences(preferences: TravelersPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const cookieValue = JSON.stringify(preferences);
    const encodedValue = encodeURIComponent(cookieValue);
    
    // Cookie'yi set et
    document.cookie = `${COOKIE_NAME}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;

    // Cookie değiştiğinde custom event gönder (diğer component'ler dinleyebilir)
    window.dispatchEvent(new CustomEvent('travelersPreferencesChanged', { detail: preferences }));

  } catch (error) {
    // Sadece development'ta log (production'da gereksiz)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[setTravelersPreferences] Error setting cookie:', error);
    }
  }
}

/**
 * Cookie'den travelers bilgisi oku
 * 
 * @returns Travelers bilgisi objesi veya null
 */
export function getTravelersPreferences(): TravelersPreferences | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(row => row.startsWith(`${COOKIE_NAME}=`));

    if (!cookie) {
      return null;
    }

    const value = cookie.split('=').slice(1).join('='); // '=' karakteri değerde olabilir
    const decodedValue = decodeURIComponent(value);
    const parsed = JSON.parse(decodedValue) as TravelersPreferences;

    // Validation: Gerekli alanlar var mı?
    if (!parsed.product_type || !Array.isArray(parsed.rooms) || parsed.rooms.length === 0) {
      return null;
    }

    // Her oda için validasyon
    const isValid = parsed.rooms.every(room => 
      typeof room.adults === 'number' && 
      typeof room.children === 'number' && 
      Array.isArray(room.childAges)
    );

    if (!isValid) {
      return null;
    }

    return parsed;
  } catch (error) {
    // Sadece development'ta log (production'da gereksiz)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[getTravelersPreferences] Error reading cookie:', error);
    }
    return null;
  }
}

/**
 * Cookie'yi temizle
 */
export function clearTravelersPreferences(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
 
  } catch (error) {
    // Sadece development'ta log (production'da gereksiz)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[clearTravelersPreferences] Error clearing cookie:', error);
    }
  }
}

/**
 * Cookie'nin varlığını kontrol et
 * 
 * @returns Cookie var mı?
 */
export function hasTravelersPreferences(): boolean {
  return getTravelersPreferences() !== null;
}

/**
 * Search filters cookie utility
 * Form filtrelerini cookie'de saklamak için
 */
const SEARCH_FILTERS_COOKIE_NAME = 'search_filters_v1';
const SEARCH_FILTERS_MAX_AGE = 86400; // 24 saat

/**
 * Cookie'ye search filters kaydet
 */
export function setSearchFilters(filters: Record<string, any>): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const cookieValue = JSON.stringify(filters);
    const encodedValue = encodeURIComponent(cookieValue);
    
    document.cookie = `${SEARCH_FILTERS_COOKIE_NAME}=${encodedValue}; path=/; max-age=${SEARCH_FILTERS_MAX_AGE}; SameSite=Lax`;
    
    // Cookie değiştiğinde custom event gönder
    window.dispatchEvent(new CustomEvent('searchFiltersChanged', { detail: filters }));
  } catch (error) {
    // Sadece development'ta log (production'da gereksiz)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[setSearchFilters] Error setting cookie:', error);
    }
  }
}

/**
 * Cookie'den search filters oku
 */
export function getSearchFilters(): Record<string, any> | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(row => row.startsWith(`${SEARCH_FILTERS_COOKIE_NAME}=`));

    if (!cookie) {
      return null;
    }

    const value = cookie.split('=').slice(1).join('=');
    const decodedValue = decodeURIComponent(value);
    const parsed = JSON.parse(decodedValue) as Record<string, any>;

    return parsed;
  } catch (error) {
    // Sadece development'ta log (production'da gereksiz)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[getSearchFilters] Error reading cookie:', error);
    }
    return null;
  }
}

/**
 * Search filters cookie'yi temizle
 */
export function clearSearchFilters(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    document.cookie = `${SEARCH_FILTERS_COOKIE_NAME}=; path=/; max-age=0`;
  } catch (error) {
    // Sadece development'ta log (production'da gereksiz)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[clearSearchFilters] Error clearing cookie:', error);
    }
  }
}

/** Rezervasyon checkout sayfasında kullanılan çerez adı (sidebar/checkout ile senkron) */
export const RESERVATION_CHECKOUT_COOKIE_NAME = 'reservation_checkout';

/** Book-now sayfasında fiyat hesaplaması için tur detayından taşınan veri (ek API isteği olmaması için) */
export const RESERVATION_PRICING_STORAGE_KEY = 'reservation_pricing';

/**
 * Sadece reservation_checkout çerezini temizle
 */
export function clearReservationCheckoutCookie(): void {
  if (typeof window === 'undefined') return;
  try {
    document.cookie = `${RESERVATION_CHECKOUT_COOKIE_NAME}=; path=/; max-age=0`;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[clearReservationCheckoutCookie]', error);
    }
  }
}

/**
 * Rezervasyon akışıyla ilgili tüm çerezleri temizle.
 * Rezervasyon tamamlandıktan veya ödeme başarılı olduktan sonra çağrılmalı;
 * yeni rezervasyonda eski veri kalmasın.
 */
export function clearReservationCookies(): void {
  clearReservationCheckoutCookie();
  clearTravelersPreferences();
}
